import LogoSrc from '../components/Logo.jpg';

/**
 * brandAssets — identidade visual compartilhada dos exports (PDF/Excel).
 * Extraído do padrão já aprovado em `components/exportUtils.js` (Folha de
 * Ponto / Espelho Individual) pra reaproveitar em outros módulos (ex.: Metas)
 * sem duplicar a lógica de conversão do logo pra base64.
 *
 * Paleta de marca: NÃO inventar cor nova — são as mesmas usadas em
 * `exportUtils.js` e no CSS do projeto.
 *   - Azul-marinho (#1f2d3d) — cabeçalhos de tabela, título.
 *   - Verde (#AFD918) — linha divisória, destaque de marca.
 * Semáforo (verde/amarelo/vermelho) segue exatamente as cores já
 * estabelecidas em `clinical.css` (.badge-verde/.badge-amarelo/.badge-vermelho).
 */

export const COMPANY_BRAND = 'DHR Gestão';

// RGB (0-255) — uso em jsPDF (setTextColor/setFillColor/setDrawColor).
export const BRAND_NAVY_RGB = [31, 45, 61];
export const BRAND_GREEN_RGB = [175, 217, 24];

// ARGB (exceljs) e hex (recharts/CSS).
export const BRAND_NAVY_ARGB = 'FF1F2D3D';
export const BRAND_NAVY_LIGHT_ARGB = 'FF33475C';
export const BRAND_GREEN_ARGB = 'FFAFD918';
export const BRAND_NAVY_HEX = '#1f2d3d';
export const BRAND_GREEN_HEX = '#AFD918';

// Semáforo — mesma paleta de `clinical.css` (.badge-verde/.badge-amarelo/.badge-vermelho).
export const SEMAFORO_RGB = {
  verde: [26, 122, 58],
  amarelo: [122, 78, 0],
  vermelho: [192, 57, 43],
};
export const SEMAFORO_ARGB = {
  verde: 'FF1A7A3A',
  amarelo: 'FF7A4E00',
  vermelho: 'FFC0392B',
};

// Tints (fundo bem claro) das cores do semáforo — mesma família de cor,
// usados como `fill` de célula no Excel (não só cor de fonte) nas colunas de
// status/semáforo das tabelas de detalhe.
export const SEMAFORO_TINT_ARGB = {
  verde: 'FFE7F5EA',
  amarelo: 'FFFAF2D9',
  vermelho: 'FFFBE7E5',
};

// Severidade de alerta (alta/media/baixa) — mesma lógica de cor usada em
// `clinical.css` (.alert-alta/.alert-media/.alert-baixa) e no padrão de
// severidade já visto em outras telas do projeto (ex.: AuditPage.js).
export const SEVERITY_RGB = {
  alta: [217, 48, 37],
  media: [122, 78, 0],
  baixa: [90, 106, 122],
};
export const SEVERITY_ARGB = {
  alta: 'FFD93025',
  media: 'FF7A4E00',
  baixa: 'FF5A6A7A',
};

// Tokens derivados da paleta de marca (mesma família de cor — NÃO são cores
// novas) usados nos cards de KPI dos exports (fundo/borda sutis).
export const BRAND_NAVY_TINT_RGB = [237, 240, 244];
export const BRAND_NAVY_TINT_ARGB = 'FFEDF0F4';
export const BRAND_NAVY_BORDER_RGB = [206, 213, 222];
export const BRAND_NAVY_BORDER_ARGB = 'FFCED5DE';

let cachedLogo; // memoiza a conversão canvas->base64 entre múltiplos exports na mesma sessão

/** Converte o logo (./Logo.jpg) para data URL base64 via canvas — reaproveitável em jsPDF (addImage) e exceljs (workbook.addImage). */
export async function getLogoBase64() {
  if (cachedLogo !== undefined) return cachedLogo;
  cachedLogo = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/jpeg'));
    };
    img.onerror = () => resolve(null);
    img.src = LogoSrc;
  });
  return cachedLogo;
}

export function genLabel() {
  return `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Captura de gráfico Recharts (html2canvas) — reusado pelos 3 módulos de
// export (Metas, Avaliação por Período, Ponto) pra embutir o gráfico já
// renderizado na tela como imagem no PDF/Excel.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Captura um elemento DOM (container de um gráfico Recharts já renderizado
 * na tela) como PNG base64. Retorna `null` se `el` for nulo/indefinido (ex.:
 * tela em estado vazio, "sem dados no período" — o container do gráfico nem
 * chega a existir) ou se a captura falhar por qualquer motivo — o chamador
 * deve tratar `null` pulando a imagem do gráfico, sem quebrar o resto do
 * export.
 */
export async function captureChartImage(el) {
  if (!el) return null;
  try {
    const [{ default: html2canvas }] = await Promise.all([import('html2canvas')]);
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Falha ao capturar gráfico para exportação:', err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// PDF (jsPDF) — blocos visuais compartilhados: cabeçalho com faixa de marca,
// rodapé com mini-marca, cards de KPI desenhados (roundedRect) e barra de
// seção — mesma linguagem visual nos 3 módulos de export.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Cabeçalho "de agência": faixa navy full-width com logo + título + subtítulo
 * em branco/verde, faixa verde grossa como divisória logo abaixo.
 *
 * Decisão sobre o logo em fundo escuro: o arquivo `Logo.jpg` já tem fundo
 * próprio quase preto com traço branco/verde — testado visualmente (ver
 * MEMORY.md deste agente) e fica legível sobre o navy da marca, então o
 * cabeçalho pode usar fundo escuro sem sacrificar legibilidade do logo.
 *
 * Retorna o Y (mm) onde o conteúdo da página deve começar.
 */
export function drawPdfHeader(doc, { logoB64, subtitle, meta, pageW, height = 34 }) {
  doc.setFillColor(...BRAND_NAVY_RGB);
  doc.rect(0, 0, pageW, height, 'F');

  const tx = logoB64 ? 34 : 10;
  if (logoB64) doc.addImage(logoB64, 'JPEG', 8, height / 2 - 10, 20, 20);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(COMPANY_BRAND, tx, 15);

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(222, 227, 233);
    doc.text(subtitle, tx, 23);
  }

  if (meta) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...BRAND_GREEN_RGB);
    doc.text(meta, tx, 29);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(190, 198, 208);
  doc.text(genLabel(), pageW - 8, 10, { align: 'right' });

  // Faixa verde grossa — divisória de marca mais forte que a linha fina anterior.
  doc.setFillColor(...BRAND_GREEN_RGB);
  doc.rect(0, height, pageW, 2, 'F');

  return height + 2 + 8;
}

/** Rodapé paginado com mini-marca (tracinho verde) consistente com o cabeçalho. */
export function drawPdfFooter(doc, brandLine) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pW = doc.internal.pageSize.getWidth();
    const pH = doc.internal.pageSize.getHeight();

    doc.setFillColor(...BRAND_GREEN_RGB);
    doc.rect(10, pH - 10.5, 8, 1, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(150, 158, 168);
    doc.text(brandLine, 21, pH - 7.3);
    doc.text(`Página ${i} de ${totalPages}`, pW - 10, pH - 7.3, { align: 'right' });
  }
}

/**
 * Barra de título de seção (navy, texto branco) — mesmo estilo já usado nos
 * cabeçalhos de tabela (`colSpan` navy em `jspdf-autotable`), só que
 * desenhada à mão para seções sem tabela (ex.: antes da imagem do gráfico).
 * Retorna o Y seguinte, com respiro.
 */
export function drawSectionBarPdf(doc, { x = 10, y, width, text, height = 7 }) {
  doc.setFillColor(...BRAND_NAVY_RGB);
  doc.rect(x, y, width, height, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(255, 255, 255);
  doc.text(text, x + 3, y + height / 1.5);
  return y + height + 4;
}

/**
 * Grid de cards de KPI desenhados com `roundedRect` — substitui a antiga
 * tabela de 2 colunas (label/valor) por algo visualmente mais próximo dos
 * cards da tela (`.perf-kpi-card`/`.clinical-kpi-card`): fundo branco, borda
 * sutil, faixa de cor lateral (semáforo, quando `card.color` for passado),
 * label pequeno em maiúsculas cinza no topo, valor grande em negrito.
 * `cards`: [{ label, value, color?: [r,g,b], sub?: string }]
 * Retorna o Y seguinte.
 */
export function drawKpiCardsPdf(doc, { startY, margin = 10, pageW, cards, perRow = 3, cardHeight = 22 }) {
  const usableW = pageW - margin * 2;
  const gap = 4;
  const cardW = (usableW - gap * (perRow - 1)) / perRow;
  let x = margin;
  let y = startY;

  cards.forEach((card, i) => {
    const col = i % perRow;
    if (col === 0 && i !== 0) { y += cardHeight + gap; x = margin; }

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...BRAND_NAVY_BORDER_RGB);
    doc.setLineWidth(0.35);
    doc.roundedRect(x, y, cardW, cardHeight, 2, 2, 'FD');

    if (card.color) {
      doc.setFillColor(...card.color);
      doc.roundedRect(x, y, 2.2, cardHeight, 1, 1, 'F');
    }

    const tx = x + (card.color ? 5.5 : 4.5);
    const maxW = cardW - (card.color ? 9 : 8);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(140, 148, 158);
    doc.text(String(card.label).toUpperCase(), tx, y + 6.5, { maxWidth: maxW });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12.5);
    doc.setTextColor(...(card.color || BRAND_NAVY_RGB));
    doc.text(String(card.value), tx, y + 14.5, { maxWidth: maxW });

    if (card.sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.3);
      doc.setTextColor(160, 168, 178);
      doc.text(String(card.sub), tx, y + cardHeight - 2.5, { maxWidth: maxW });
    }

    x += cardW + gap;
  });

  return y + cardHeight + 8;
}

/** Insere a imagem do gráfico capturado (`captureChartImage`) dimensionada
 * pra largura útil da página, mantendo a proporção original. `chart` pode
 * ser `null` (nesse caso não desenha nada e retorna `y` sem alterar). */
export function addChartImagePdf(doc, { chart, x = 10, y, maxWidth }) {
  if (!chart || !chart.dataUrl) return y;
  const ratio = chart.height / chart.width;
  const imgW = maxWidth;
  const imgH = imgW * ratio;
  doc.addImage(chart.dataUrl, 'PNG', x, y, imgW, imgH);
  return y + imgH + 6;
}

/** Garante espaço vertical mínimo antes de desenhar um bloco (seção +
 * imagem, por exemplo) que `jspdf-autotable` não pagina automaticamente —
 * se não couber, adiciona página e volta a `topMargin`. */
export function ensurePdfSpace(doc, cursorY, needed, topMargin = 15) {
  const pH = doc.internal.pageSize.getHeight();
  if (cursorY + needed > pH - 16) {
    doc.addPage();
    return topMargin;
  }
  return cursorY;
}

// ─────────────────────────────────────────────────────────────────────────
// Excel (exceljs) — blocos visuais compartilhados equivalentes aos de PDF:
// barra de seção, cards de KPI (células com fill/borda) e imagem do gráfico.
// ─────────────────────────────────────────────────────────────────────────

/** Barra de título de seção navy full-width (mesmo padrão já usado nos 3
 * arquivos de export antes desta mudança). Retorna a próxima linha livre. */
export function xlsxSectionHeader(ws, r, text, span) {
  for (let c = 1; c <= span; c++) {
    const cell = ws.getCell(r, c);
    cell.value = c === 1 ? text : '';
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_ARGB } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
  }
  ws.mergeCells(r, 1, r, span);
  return r + 1;
}

/**
 * Bloco de KPIs como "cards": cada KPI ocupa um bloco de 2 linhas (label +
 * valor) mesclado horizontalmente, com fundo em tint do navy e borda sutil
 * ao redor — imita visualmente os cards do PDF (`drawKpiCardsPdf`) e da tela.
 * `cards`: [{ label, value, colorArgb?: 'FFrrggbb' }]
 * Retorna a próxima linha livre (com uma linha em branco de respiro).
 */
export function xlsxKpiCards(ws, startRow, cards, { totalCols = 6, perRow = 3 } = {}) {
  const cardCols = Math.max(1, Math.floor(totalCols / perRow));
  let r = startRow;

  cards.forEach((card, i) => {
    const col = i % perRow;
    if (col === 0 && i !== 0) r += 3; // bloco de 2 linhas + 1 de respiro entre fileiras de cards
    const c1 = col * cardCols + 1;
    const c2 = c1 + cardCols - 1;
    const labelRow = r;
    const valueRow = r + 1;

    for (let cc = c1; cc <= c2; cc++) {
      for (let rr = labelRow; rr <= valueRow; rr++) {
        const cell = ws.getCell(rr, cc);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_TINT_ARGB } };
        cell.border = {
          top: rr === labelRow ? { style: 'thin', color: { argb: BRAND_NAVY_BORDER_ARGB } } : undefined,
          bottom: rr === valueRow ? { style: 'thin', color: { argb: BRAND_NAVY_BORDER_ARGB } } : undefined,
          left: cc === c1 ? { style: 'thin', color: { argb: BRAND_NAVY_BORDER_ARGB } } : undefined,
          right: cc === c2 ? { style: 'thin', color: { argb: BRAND_NAVY_BORDER_ARGB } } : undefined,
        };
      }
    }

    ws.mergeCells(labelRow, c1, labelRow, c2);
    ws.mergeCells(valueRow, c1, valueRow, c2);

    const labelCell = ws.getCell(labelRow, c1);
    labelCell.value = String(card.label).toUpperCase();
    labelCell.font = { size: 8, bold: true, color: { argb: 'FF828A94' } };
    labelCell.alignment = { vertical: 'middle' };

    const valueCell = ws.getCell(valueRow, c1);
    valueCell.value = card.value;
    valueCell.font = { size: 13, bold: true, color: { argb: card.colorArgb || BRAND_NAVY_ARGB } };
    valueCell.alignment = { vertical: 'middle' };
  });

  const rows = Math.ceil(cards.length / perRow);
  return startRow + rows * 3 + 1;
}

/** Embute a imagem do gráfico capturado (`captureChartImage`) na planilha,
 * mesmo padrão já usado pro logo (`wb.addImage`/`ws.addImage`), só que maior.
 * `chart` pode ser `null` — nesse caso não insere nada e retorna `row` igual.
 * Retorna a próxima linha livre (estimada a partir da altura em px). */
export function addChartImageXlsx(wb, ws, { chart, row, col = 0, widthPx = 620 }) {
  if (!chart || !chart.dataUrl) return row;
  const ratio = chart.height / chart.width;
  const w = widthPx;
  const h = w * ratio;
  const imgId = wb.addImage({ base64: chart.dataUrl, extension: 'png' });
  ws.addImage(imgId, { tl: { col, row: row - 1 }, ext: { width: w, height: h } });
  const rowsUsed = Math.ceil(h / 20) + 2;
  return row + rowsUsed;
}
